import { Card, CardContent } from "@material-ui/core";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";
import { observer } from "mobx-react";
import moment from "moment";
import * as React from "react";
import { DIALOG_TRANSITION_TIME } from "../../config";
import { getJobCategoryOptions } from "../../constants";
import { t } from "../../i18n/util";
import { API } from "../../network/API";
import { GetApplicantsObject, GetApplicantsObjectInterviewDates } from "../../network/APITypes";
import { generalStore } from "../../stores/GeneralStore";
import { THEME } from "../../theme/ThemeStore";
import { JobCategoryEnum } from "../../types/models";
import { history, pushRoute } from "../app/router/history";
import { HrRoutes } from "../hr/router/HrRoutes";
import { jobCategoryToColors, localeToFormat, optionToString } from "../util/helpers";
import { ConfirmationDialog } from "./ConfirmationDialog";
import { ApplicantPicture, ApplicationDateChip, EvidenceTag, NoApplicantPicture } from "./Primitives";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";

type Props = { item: GetApplicantsObject; switchColumnsChangeToggle: () => void };

export const ApplicantCard = observer(({ item, switchColumnsChangeToggle }: Props) => {
    const [openDialog, setOpenDialog] = React.useState<GetApplicantsObjectInterviewDates | undefined>();

    const publicUrl = process.env.PUBLIC_URL;

    const handleOpen = (notificaton: GetApplicantsObjectInterviewDates) => {
        setOpenDialog(notificaton);
    };

    const handleSubmit = () => {
        const confirmAppointment = async () => {
            try {
                generalStore.isLoading = true;
                if (!openDialog?.id) {
                    throw new Error(t("error.noAppointmendId"));
                }
                await API.postAppointment(item.id, openDialog.id);
            } catch (error) {
                generalStore.setError(t("error.confirmAppointment"), error);
            } finally {
                generalStore.isLoading = false;
                switchColumnsChangeToggle();
            }
        };

        confirmAppointment();

        setTimeout(() => setOpenDialog(undefined), DIALOG_TRANSITION_TIME);
    };

    const handleClose = () => {
        setTimeout(() => setOpenDialog(undefined), DIALOG_TRANSITION_TIME);
    };

    const dateToBGColor = (date: GetApplicantsObjectInterviewDates) => {
        if (date?.confirmed) {
            return THEME.customColors.confirmed;
        }
        if (date?.preferred) {
            return THEME.customColors.preferred;
        }
    };

    const hasConfirmedInterview = item.interviewDates?.some((interviewDate) => interviewDate.confirmed);

    return (
        <>
            <Card style={{ flexGrow: 1 }}>
                <CardContent
                    className="applicantCard"
                    style={{
                        padding: "8px 16px 5px",
                        borderLeft: `4px solid ${jobCategoryToColors(item.jobCategory as JobCategoryEnum).primary}`,
                        borderRadius: item.interviewDates ? "4px 4px 0 0" : 4,
                        position: "relative"
                    }}
                    onClick={() => {
                        pushRoute(HrRoutes.APPLICANT.DETAILS, {
                            params: { applicantId: item.id }
                        });
                    }}
                >
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                        <div>
                            <div
                                className="body2semibold"
                                style={{
                                    color: THEME.customColors.headingsDark,
                                    wordBreak: "break-word",
                                    lineHeight: "18px",
                                    display: "flex",
                                    alignItems: "flex-start"
                                }}
                            >
                                {!item.applicationRead && (
                                    <FiberManualRecordIcon
                                        color="primary"
                                        style={{ marginTop: 3, marginRight: 8, height: 9, width: 9 }}
                                    />
                                )}
                                {item.firstName} {item.lastName}
                            </div>
                            <div
                                className="caption"
                                style={{ color: THEME.customColors.body1Dark, lineHeight: "18px" }}
                            >
                                {optionToString(getJobCategoryOptions(), item.jobCategory)}
                            </div>
                        </div>

                        <OpenInNewIcon
                            style={{ cursor: "pointer" }}
                            onClick={(event) => {
                                event.stopPropagation();

                                window.open(
                                    (publicUrl ?? "") + HrRoutes.APPLICANT.DETAILS.replace(":applicantId", item.id),
                                    "_blank"
                                );
                            }}
                        />
                    </div>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom: 6,
                            gap: 6,
                            color: THEME.customColors.body1Dark
                        }}
                        className="caption"
                    >
                        <img src={THEME.Images.icCalender} alt="" />
                        {moment(item.startDate).format(t("screen.dateFormat"))}
                        <img src={THEME.Images.icCoins} alt="" />
                        <div
                            style={{
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                                width: 0,
                                flexGrow: 1
                            }}
                        >
                            {new Intl.NumberFormat(localeToFormat(generalStore.locale)).format(item.salary)}
                        </div>
                    </div>
                    {item.evidence && <EvidenceTag>{t("screen.dashboard.evidence")}</EvidenceTag>}
                    <div style={{ color: THEME.customColors.body2Dark, fontSize: 9, lineHeight: "20px" }}>
                        {t("screen.dashboard.applicationSubmitted")}{" "}
                        {moment(item.applicationDate).format(t("screen.dateFormat"))}
                    </div>
                    {!item.emailSentExternal && item.applicationState !== "application_received" && (
                        <img
                            src={THEME.Images.icNoMessage}
                            alt=""
                            style={{ position: "absolute", bottom: 5, right: 16, height: 16, width: 16 }}
                        />
                    )}
                </CardContent>
                {item.interviewDates && (
                    <CardContent
                        className="applicantCardInterviews"
                        style={{
                            padding: "10px 20px",
                            borderRadius: "0 0 4px 4px",
                            display: "flex",
                            gap: 8,
                            justifyContent: "space-evenly"
                        }}
                    >
                        {item.interviewDates.map((interviewDate, index) => (
                            <ApplicationDateChip
                                key={index}
                                style={{
                                    backgroundColor: dateToBGColor(interviewDate),
                                    cursor: !hasConfirmedInterview && interviewDate.preferred ? "pointer" : "default"
                                }}
                                date={interviewDate}
                                onClick={() => {
                                    if (!hasConfirmedInterview && interviewDate.preferred) {
                                        handleOpen(interviewDate);
                                    }
                                }}
                            />
                        ))}
                    </CardContent>
                )}
            </Card>
            <ConfirmationDialog
                isOpen={!!openDialog}
                onBack={handleClose}
                onSubmit={handleSubmit}
                header={t("screen.appointmentConfirmation.confirmSingleAppointment.header")}
                submitButtonText={t("screen.dashboard.confirm")}
                label={
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            textAlign: "start"
                        }}
                    >
                        {t("screen.appointmentConfirmation.confirmSingleAppointment.text")}
                        <br />
                        <br />
                        {`${moment(openDialog?.startTime).format(t("screen.dateTimeFormat"))} - ${moment(
                            openDialog?.endTime
                        ).format(t("screen.timeFormat"))}`}
                    </div>
                }
            />
        </>
    );
});
